import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import { GET, loading, format_date, Select } from "../components/CustomComponent";

const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  borderRadius: 24,
  boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)",
};

const kpiCardStyle = {
  borderRadius: 24,
  border: "1px solid rgba(255, 255, 255, 0.55)",
  boxShadow: "0 20px 40px rgba(12, 60, 120, 0.12)",
  position: "relative",
  overflow: "hidden",
};

const counterCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
};

const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const CATEGORY_COLORS = ["#0c75e6", "#2fd5e4", "#f5a623", "#8d5cf6", "#ef5da8", "#2fb572"];

const defaultOverview = {
  finance: {
    income_amount: 0,
    income_net: 0,
    expense_amount: 0,
    expense_net: 0,
    balance: 0,
    net_profit: 0,
    corporate_tax: 0,
    corporate_tax_due: 0,
    vat_payable: 0,
    transaction_count: 0,
  },
  monthly: [],
  expense_by_category: [],
  counters: { employees: 0, active_employees: 0, pending_leaves: 0, pending_transactions: 0, documents: 0, partners: 0 },
  upcoming_holidays: [],
  latest_payroll: null,
  recent_transactions: [],
};

const formatMoney = (value) => (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCurrency = (value) => `฿ ${formatMoney(value)}`;
const formatCompact = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(num / 1_000)}K`;
  return String(Math.round(num));
};

export default function Dashboard() {
  const { title, description } = appModules["dashboard"];
  const navigate = useNavigate();

  const [overview, setOverview] = useState(defaultOverview);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/dashboard", { year });
    if (result?.status && result.data) {
      setOverview({ ...defaultOverview, ...result.data });
    }
    loading(false);
  }, [year]);

  useEffect(() => {
    getData();
  }, [getData]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 4 }, (_, index) => ({ value: String(current - index), label: `ปี ${current - index}` }));
  }, []);

  const finance = overview.finance || defaultOverview.finance;
  const counters = overview.counters || defaultOverview.counters;

  const kpiCards = [
    {
      label: "รายรับสะสมทั้งปี",
      value: formatCurrency(finance.income_net),
      hint: `${finance.transaction_count} ธุรกรรมในปีนี้`,
      icon: "fa-arrow-trend-up",
      gradient: "linear-gradient(135deg, #0c75e6 0%, #2fd5e4 100%)",
      link: "/finance-transactions",
    },
    {
      label: "รายจ่ายสะสมทั้งปี",
      value: formatCurrency(finance.expense_net),
      hint: "ยอดสุทธิที่จ่ายจริง",
      icon: "fa-arrow-trend-down",
      gradient: "linear-gradient(135deg, #f5577a 0%, #f5a623 100%)",
      link: "/finance-transactions",
    },
    {
      label: "คงเหลือ",
      value: formatCurrency(finance.balance),
      hint: "รายรับสุทธิ − รายจ่ายสุทธิ",
      icon: "fa-scale-balanced",
      gradient: "linear-gradient(135deg, #0b6b3a 0%, #2fb572 100%)",
      link: "/finance-summary",
    },
    {
      label: "ภาษีนิติบุคคลคาดการณ์",
      value: formatCurrency(finance.corporate_tax_due),
      hint: `กำไรสุทธิ ${formatCurrency(finance.net_profit)}`,
      icon: "fa-file-invoice-dollar",
      gradient: "linear-gradient(135deg, #5843f3 0%, #8d5cf6 100%)",
      link: "/finance-summary",
    },
  ];

  const counterCards = [
    { label: "พนักงานทั้งหมด", value: counters.employees, sub: `ทำงานอยู่ ${counters.active_employees} คน`, color: "#0c76e5", icon: "fa-users", link: "/employees" },
    { label: "คำขอลารออนุมัติ", value: counters.pending_leaves, sub: counters.pending_leaves > 0 ? "ต้องดำเนินการ" : "ไม่มีค้าง", color: counters.pending_leaves > 0 ? "#b97007" : "#188251", icon: "fa-calendar-check", link: "/leave" },
    { label: "ธุรกรรมรออนุมัติ", value: counters.pending_transactions, sub: counters.pending_transactions > 0 ? "ต้องดำเนินการ" : "ไม่มีค้าง", color: counters.pending_transactions > 0 ? "#b97007" : "#188251", icon: "fa-money-check-dollar", link: "/finance-transactions" },
    { label: "เอกสารการเงิน", value: counters.documents, sub: `ลูกค้า/คู่ค้า ${counters.partners} ราย`, color: "#8d5cf6", icon: "fa-folder-open", link: "/company-documents" },
  ];

  const monthly = overview.monthly || [];
  const maxMonthlyValue = Math.max(1, ...monthly.map((item) => Math.max(item.income, item.expense)));
  const totalExpenseByCategory = (overview.expense_by_category || []).reduce((sum, item) => sum + item.amount, 0);

  return (
    <AppShell title={title} description={description}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <span className="badge rounded-pill px-3 py-2" style={{ background: "rgba(12, 117, 230, 0.10)", color: "#0c75e6", fontWeight: 600 }}>
          <i className="fa-solid fa-calendar-day me-2" />
          ข้อมูลจริงประจำปี {overview.year || year}
        </span>
        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary small">เลือกปี</span>
          <div style={{ width: 150 }}>
            <Select className="form-control" value={year} options={yearOptions} placeholder="เลือกปี" onChange={(value) => setYear(value)} />
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {kpiCards.map((card) => (
          <div className="col-12 col-md-6 col-xl-3" key={card.label}>
            <div
              className="p-4 h-100 text-white"
              style={{ ...kpiCardStyle, background: card.gradient, cursor: "pointer" }}
              onClick={() => navigate(card.link)}
            >
              <div
                style={{ position: "absolute", top: -30, right: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.12)" }}
              />
              <div className="d-flex justify-content-between align-items-start">
                <p className="small text-uppercase mb-3" style={{ letterSpacing: "0.12em", opacity: 0.92 }}>{card.label}</p>
                <div className="d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.20)" }}>
                  <i className={`fa-solid ${card.icon}`} />
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: "1.5rem" }}>{card.value}</h3>
              <div className="small" style={{ opacity: 0.85 }}>{card.hint}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        {counterCards.map((card) => (
          <div className="col-6 col-xl-3" key={card.label}>
            <div
              className="h-100 rounded-4 p-4 d-flex align-items-center gap-3"
              style={{ ...counterCardStyle, cursor: "pointer" }}
              onClick={() => navigate(card.link)}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}1f`, color: card.color, fontSize: 18 }}
              >
                <i className={`fa-solid ${card.icon}`} />
              </div>
              <div>
                <p className="small text-uppercase text-secondary mb-1">{card.label}</p>
                <h3 className="h4 mb-0 fw-bold" style={{ color: card.color }}>{card.value}</h3>
                <div className="small text-secondary">{card.sub}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12 col-xl-8">
          <div className="p-3 p-md-4 h-100" style={sectionCardStyle}>
            <h5 className="fw-semibold mb-1">รายรับ vs รายจ่าย รายเดือน</h5>
            <p className="text-secondary small mb-4">ยอดก่อน VAT ของแต่ละเดือนในปี {overview.year || year}</p>

            {/* กราฟแท่งวาดด้วย CSS ไม่ต้องพึ่งไลบรารีเพิ่ม */}
            <div className="d-flex align-items-end gap-2 gap-md-3" style={{ minHeight: 220, overflowX: "auto" }}>
              {monthly.map((item, index) => (
                <div key={item.month} className="d-flex flex-column align-items-center flex-grow-1" style={{ minWidth: 34 }}>
                  <div className="d-flex align-items-end gap-1 w-100 justify-content-center" style={{ height: 180 }}>
                    <div
                      title={`รายรับ ${formatCurrency(item.income)}`}
                      style={{
                        width: 12,
                        height: `${(item.income / maxMonthlyValue) * 100}%`,
                        minHeight: item.income > 0 ? 4 : 0,
                        borderRadius: 6,
                        background: "linear-gradient(180deg, #2fd5e4 0%, #0c75e6 100%)",
                      }}
                    />
                    <div
                      title={`รายจ่าย ${formatCurrency(item.expense)}`}
                      style={{
                        width: 12,
                        height: `${(item.expense / maxMonthlyValue) * 100}%`,
                        minHeight: item.expense > 0 ? 4 : 0,
                        borderRadius: 6,
                        background: "linear-gradient(180deg, #f5a623 0%, #f5577a 100%)",
                      }}
                    />
                  </div>
                  <div className="small text-secondary mt-2">{THAI_MONTHS[index]}</div>
                </div>
              ))}
            </div>

            <div className="d-flex gap-4 mt-3 small">
              <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, borderRadius: 3, background: "#0c75e6" }} /> รายรับ</span>
              <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, borderRadius: 3, background: "#f5577a" }} /> รายจ่าย</span>
              <span className="text-secondary ms-auto">สูงสุด {formatCompact(maxMonthlyValue)}</span>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="p-3 p-md-4 h-100" style={sectionCardStyle}>
            <h5 className="fw-semibold mb-1">ค่าใช้จ่ายตามหมวดหมู่</h5>
            <p className="text-secondary small mb-3">6 อันดับแรกของปีนี้</p>

            {(overview.expense_by_category || []).length > 0 ? (
              overview.expense_by_category.map((item, index) => {
                const percent = totalExpenseByCategory > 0 ? (item.amount / totalExpenseByCategory) * 100 : 0;
                return (
                  <div className="mb-3" key={item.category_name}>
                    <div className="d-flex justify-content-between align-items-baseline">
                      <span className="fw-semibold" style={{ color: "#22384a" }}>{item.category_name}</span>
                      <span className="small text-secondary">{formatMoney(item.amount)}</span>
                    </div>
                    <div className="progress mt-1" style={{ height: 6, background: "rgba(15,102,182,0.08)" }}>
                      <div className="progress-bar" style={{ width: `${percent}%`, background: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-secondary small py-4 text-center">ยังไม่มีข้อมูลค่าใช้จ่าย</div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-4">
          <div className="p-3 p-md-4 h-100" style={sectionCardStyle}>
            <h6 className="fw-semibold mb-3"><i className="fa-solid fa-umbrella-beach me-2" style={{ color: "#0c76e5" }} />วันหยุดที่กำลังจะถึง</h6>
            {(overview.upcoming_holidays || []).length > 0 ? (
              overview.upcoming_holidays.map((item) => (
                <div className="d-flex align-items-center gap-3 mb-3" key={item.uid}>
                  <div
                    className="text-center fw-bold flex-shrink-0"
                    style={{ width: 46, padding: "6px 0", borderRadius: 10, background: "rgba(12,117,230,0.10)", color: "#0b4f84" }}
                  >
                    {String(item.holiday_date).slice(8, 10)}
                    <div className="small fw-normal">{THAI_MONTHS[Number(String(item.holiday_date).slice(5, 7)) - 1]}</div>
                  </div>
                  <div>
                    <div className="fw-semibold" style={{ color: "#22384a" }}>{item.name}</div>
                    <div className="small text-secondary">{format_date(item.holiday_date, "dd/mm/yyyy")}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-secondary small py-3">ยังไม่ได้ตั้งวันหยุด ไปตั้งที่ ตั้งค่าระบบ &gt; วันหยุดบริษัท</div>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="p-3 p-md-4 h-100" style={sectionCardStyle}>
            <h6 className="fw-semibold mb-3"><i className="fa-solid fa-money-check-dollar me-2" style={{ color: "#188251" }} />รอบเงินเดือนล่าสุด</h6>
            {overview.latest_payroll ? (
              <>
                <div className="fw-bold" style={{ fontSize: "1.4rem", color: "#0b4f84" }}>{overview.latest_payroll.period_month || "-"}</div>
                <div className="small text-secondary mb-3">{overview.latest_payroll.payroll_code || "-"}</div>
                {[
                  { label: "จำนวนพนักงาน", value: `${overview.latest_payroll.total_employees || 0} คน` },
                  { label: "ยอดจ่ายสุทธิ", value: formatCurrency(overview.latest_payroll.total_net) },
                  { label: "วันที่จ่าย", value: overview.latest_payroll.pay_date ? format_date(overview.latest_payroll.pay_date, "dd/mm/yyyy") : "-" },
                  { label: "สถานะ", value: overview.latest_payroll.status === "success" ? "อนุมัติแล้ว" : overview.latest_payroll.status === "cancelled" ? "ยกเลิก" : "รออนุมัติ" },
                ].map((row) => (
                  <div className="d-flex justify-content-between border-bottom py-2" key={row.label}>
                    <span className="text-secondary small">{row.label}</span>
                    <span className="fw-semibold">{row.value}</span>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-secondary small py-3">ยังไม่มีรอบเงินเดือน</div>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="p-3 p-md-4 h-100" style={sectionCardStyle}>
            <h6 className="fw-semibold mb-3"><i className="fa-solid fa-receipt me-2" style={{ color: "#8d5cf6" }} />ภาระภาษีคงค้าง</h6>
            {[
              { label: "VAT ค้างนำส่ง", value: formatCurrency(finance.vat_payable) },
              { label: "WHT ที่หักไว้ (รายจ่าย)", value: formatCurrency(finance.expense_wht) },
              { label: "ภาษีนิติบุคคลทั้งก้อน", value: formatCurrency(finance.corporate_tax) },
              { label: "ภาษีที่ต้องจ่ายเพิ่ม", value: formatCurrency(finance.corporate_tax_due) },
            ].map((row) => (
              <div className="d-flex justify-content-between border-bottom py-2" key={row.label}>
                <span className="text-secondary small">{row.label}</span>
                <span className="fw-semibold">{row.value}</span>
              </div>
            ))}
            <div className="small text-secondary mt-3">คำนวณจากธุรกรรมที่บันทึกไว้ในปีนี้ ยังไม่ใช่ตัวเลขที่ยื่นจริง</div>
          </div>
        </div>
      </div>

      <div className="p-3 p-md-4" style={sectionCardStyle}>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
          <h5 className="fw-semibold mb-0">ธุรกรรมล่าสุด</h5>
          <button className="btn btn-sm btn-outline-primary rounded-3" onClick={() => navigate("/finance-transactions")}>ดูทั้งหมด</button>
        </div>

        <div className="table-responsive table-mobile-cards">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium text-nowrap">วันที่</th>
                <th className="text-secondary fw-medium">รหัส</th>
                <th className="text-secondary fw-medium text-nowrap">ประเภท</th>
                <th className="text-secondary fw-medium">หมวดหมู่</th>
                <th className="text-secondary fw-medium">คู่ค้า</th>
                <th className="text-secondary fw-medium text-end">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              {(overview.recent_transactions || []).length > 0 ? (
                overview.recent_transactions.map((item) => {
                  const isIncome = item.transaction_type === "income";
                  return (
                    <tr key={item.uid}>
                      <td data-label="วันที่" className="text-nowrap">{format_date(item.transaction_date, "dd/mm/yyyy")}</td>
                      <td data-label="รหัส" className="fw-semibold" style={{ color: "#0b4f84" }}>{item.transaction_code || "-"}</td>
                      <td data-label="ประเภท" className="text-nowrap">
                        <span
                          className="rounded-pill px-3 py-1 small fw-semibold text-nowrap d-inline-block"
                          style={isIncome ? { background: "rgba(47, 181, 114, 0.14)", color: "#188251" } : { background: "rgba(229, 83, 83, 0.14)", color: "#b63636" }}
                        >
                          {isIncome ? "รายรับ" : "รายจ่าย"}
                        </span>
                      </td>
                      <td data-label="หมวดหมู่">{item.category_name || "-"}</td>
                      <td data-label="คู่ค้า">{item.partner_name || "-"}</td>
                      <td data-label="จำนวนเงิน" className="text-end fw-bold" style={{ color: isIncome ? "#188251" : "#b63636" }}>
                        {isIncome ? "+" : "-"}{formatMoney(item.amount_net)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-secondary py-5">ยังไม่มีธุรกรรม</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
